import { createPortal } from "react-dom";
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

import notDataImage from "@/views/assets/images/notData.png";

export interface AdminTableEmptyProps {
    /** 空态下方的提示文案，允许适配器保留业务页面的自定义文案。 */
    text?: ReactNode;
}

/** 统一复用旧 Vue ProTable 的 notData.png 空表展示。 */
export function AdminTableEmpty({ text = "暂无数据" }: AdminTableEmptyProps) {
    const anchorRef = useRef<HTMLSpanElement>(null);
    const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
    const [isStandalone, setIsStandalone] = useState(false);
    const [viewportStyle, setViewportStyle] = useState<CSSProperties>({ display: "none" });

    useLayoutEffect(() => {
        const anchor = anchorRef.current;
        if (!anchor) {
            setIsStandalone(true);
            return;
        }

        // 空态原本位于宽表的 placeholder 单元格内；把展示层提升到表格 body，彻底脱离横向滚动内容。
        const tableWrapper = anchor.closest<HTMLElement>(".ant-table-wrapper");
        const bodyHost = anchor.closest<HTMLElement>(".zh-table__body") ?? tableWrapper;
        if (!bodyHost) {
            // 统计卡片等非表格场景也复用该组件，此时保留普通文档流展示。
            setIsStandalone(true);
            return;
        }

        const syncViewport = () => {
            const scrollViewport = bodyHost.querySelector<HTMLElement>(".ant-table-body, .ant-table-content");
            if (!scrollViewport) return;

            const hostRect = bodyHost.getBoundingClientRect();
            const viewportRect = scrollViewport.getBoundingClientRect();
            const headerRect = bodyHost.querySelector<HTMLElement>(".ant-table-thead")?.getBoundingClientRect();
            const top = Math.max(viewportRect.top, headerRect?.bottom ?? viewportRect.top) - hostRect.top;
            const bottom = viewportRect.bottom - hostRect.top;

            setViewportStyle({
                left: viewportRect.left - hostRect.left,
                top,
                width: viewportRect.width,
                height: Math.max(0, bottom - top)
            });
        };

        setPortalHost(bodyHost);
        syncViewport();
        window.addEventListener("resize", syncViewport);

        // 表格列、卡片尺寸或分页变化时重新测量；横向滚动只改变 scrollLeft，不会改变 overlay 的定位基准。
        const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(syncViewport);
        resizeObserver?.observe(bodyHost);
        const scrollViewport = bodyHost.querySelector<HTMLElement>(".ant-table-body, .ant-table-content");
        if (scrollViewport) resizeObserver?.observe(scrollViewport);

        return () => {
            window.removeEventListener("resize", syncViewport);
            resizeObserver?.disconnect();
        };
    }, []);

    const emptyContent = (className: string, style?: CSSProperties) => (
        <div className={className} style={style}>
            <div className="admin-table-empty">
                <img src={notDataImage} alt="暂无数据" />
                <div>{text}</div>
            </div>
        </div>
    );

    return (
        <>
            {/* Ant Table 仍需保留一个空节点触发 empty 状态，实际 icon 通过 Portal 渲染到 bodyHost。 */}
            <span ref={anchorRef} className="admin-table-empty-anchor" aria-hidden="true" />
            {portalHost
                ? createPortal(
                      emptyContent("zh-table__empty-viewport admin-table-empty-overlay", viewportStyle),
                      portalHost
                  )
                : isStandalone
                  ? emptyContent("zh-table__empty-viewport admin-table-empty-inline")
                  : null}
        </>
    );
}

export default AdminTableEmpty;
